/**
 * Manually derived from: https://shopify.dev/api/admin/rest/reference/orders/refund
 * API Version: 2022-01
 */

import { FieldType } from "../../type-system/FieldType";
import {
  ShopifyExchangeLineItemKey,
  ShopifyExchangeLineItemToOrderLineItemKey,
  ShopifyExchangeLineItemToReturnKey,
  ShopifyExchangeLineItemToShopKey,
  ShopifyOrderLineItemKey,
  ShopifyReturnKey,
  ShopifyShopKey,
} from "./keys";
import type { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";

// Hardcoded field keys to preserve deterministic keys when shopifyFieldKey was added
const orderLineItemFieldKey = "ModelField-Shopify-ExchangeLineItem-OrderLineItem";
const orderLineItemStorageEpochKey = "FieldStorageEpoch-DataModel-Shopify-ExchangeLineItem-lineItem-initial";

const specs: ConnectionModelSpecVersion = {
  key: ShopifyExchangeLineItemKey,
  "2024-07": {
    name: "Shopify Exchange Line Item",
    fields: [
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "id", graphQLFieldKey: "id" } },
      },
      {
        name: "Return",
        fieldType: FieldType.BelongsTo,
        key: ShopifyExchangeLineItemToReturnKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "return_id", graphQLFieldKey: "returnId", skipGraphQLSelection: true },
        },
        configuration: {
          relatedModelKey: ShopifyReturnKey,
        },
      },
      {
        name: "Order Line Item",
        fieldType: FieldType.BelongsTo,
        key: ShopifyExchangeLineItemToOrderLineItemKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: { graphQLFieldKey: "lineItem", graphQLSelection: "id" },
        },
        configuration: {
          relatedModelKey: ShopifyOrderLineItemKey,
        },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifyExchangeLineItemToShopKey,
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    syncConfig: {
      type: "graphql_sublist",
      parent: "order.returns",
      resource: "return.exchangeLineItems",
      additionalFields: ["returnId:id"],
    },
  },
  "2025-04": {
    fields: [
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "id",
            graphQLFieldKey: "id",
            availableInWebhook: ["returns/approve"],
          },
        },
      },
      {
        name: "Order Line Item",
        fieldType: FieldType.BelongsTo,
        key: orderLineItemFieldKey,
        initialStorageEpochKey: orderLineItemStorageEpochKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "line_item",
            graphQLFieldKey: "lineItem",
            graphQLSelection: "id",
            availableInWebhook: ["returns/approve"],
          },
        },
        configuration: {
          relatedModelKey: ShopifyOrderLineItemKey,
        },
      },
    ],
    syncConfig: {
      type: "graphql_sublist",
      parent: "order.returns",
      resource: "return.exchangeLineItems",
      additionalFields: ["returnId:id"],
    },
  },
  "2026-10": {
    fields: [
      {
        name: "Product ID",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "productId",
            graphQLOnlyField: true,
          },
        },
      },
      {
        name: "Title",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "title",
            graphQLOnlyField: true,
          },
        },
      },
      {
        name: "Variant SKU",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "variantSku",
            graphQLOnlyField: true,
          },
        },
      },
      {
        name: "Variant Title",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "variantTitle",
            graphQLOnlyField: true,
          },
        },
      },
    ],
  },
};

export const ShopifyExchangeLineItem = ({
  apiVersion,
  frameworkVersionConstraint,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint: string;
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint);
